import { tx } from "../lib/i18n";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CalendarDays, Clock, Globe, Users, ShieldCheck, ArrowLeft, Video } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { fmtFull, fmtTime, money, monthLabel, fmtDayName } from "../lib/format";
import { Avatar } from "../components/site/Placeholder";
import { SeatsText } from "../components/site/LessonCard";
import { Spinner } from "../components/site/Layout";

const scheduleLabel = (l, lang) => {
  const s = l.schedule;
  if (l.schedule_type === "weekly") return tx("Wekelijks op {d} om {t} ({m} min) – per maand te boeken", { d: fmtDayName(l.next_start || new Date().toISOString(), lang).toLowerCase(), t: s.time, m: s.duration });
  if (l.schedule_type === "block") return tx("Cursusblok van {n} lessen ({m} min) – als één pakket te boeken", { n: s.dates.length, m: s.duration });
  return tx("Losse les van {m} minuten", { m: s.duration });
};

const UnitOption = ({ u, l, selected, onSelect, lang }) => (
  <button type="button" onClick={() => onSelect(u.key)} disabled={u.seats_left <= 0} data-testid={`unit-option-${u.key}`}
    className={`flex w-full items-center justify-between gap-3 rounded-xl border-2 px-4 py-3 text-left transition-colors disabled:opacity-50 ${selected ? "border-osk-blue bg-osk-light" : "border-slate-200 hover:border-osk-blue/50"}`}>
    <div className="text-sm">
      <p className="font-bold text-osk-navy">
        {l.schedule_type === "weekly" ? `${monthLabel(u.month, lang)} · ${u.count} ${tx("lessen")}` : l.schedule_type === "block" ? `${tx("Volledig pakket")} · ${u.count} ${tx("lessen")}` : fmtFull(u.label, lang)}
      </p>
      <p className="text-xs"><SeatsText n={u.seats_left} /></p>
    </div>
    <span className="font-extrabold text-osk-navy">{money(u.price, l.currency)}</span>
  </button>
);

export default function LessonDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [l, setL] = useState(null);
  const [children, setChildren] = useState([]);
  const [child, setChild] = useState("");
  const [unit, setUnit] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/lessons/${id}`).then((r) => { setL(r.data); setUnit(r.data.units.find((u) => u.seats_left > 0)?.key || ""); }).catch(() => setL(false)); }, [id]);
  useEffect(() => {
    if (user?.role === "parent") api.get("/parent/children").then((r) => { setChildren(r.data); setChild(r.data[0]?.id || ""); });
  }, [user]);

  if (l === null) return <Spinner />;
  if (l === false) return <div className="osk-container py-20 text-center">{tx("Les niet gevonden.")} <Link className="text-osk-blue" to="/lessen">{tx("Terug naar lessen")}</Link></div>;

  const book = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/parent/bookings", { lesson_id: l.id, child_id: child, unit_key: unit });
      nav(`/boeken/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="osk-container py-8" data-testid="lesson-detail-page">
      <Link to="/lessen" className="mb-6 inline-flex items-center gap-2 text-sm font-bold text-osk-blue"><ArrowLeft className="h-4 w-4" />{tx("Alle lessen")}</Link>
      <div className="grid gap-8 lg:grid-cols-[1fr_400px]">
        <div className="space-y-6">
          <div className="osk-rise">
            <div className="mb-3 flex flex-wrap gap-2">
              {[l.subject, l.school_type, l.grade, l.format, l.language].map((x) => <span key={x} className="osk-pill bg-osk-light text-osk-navy">{tx(x)}</span>)}
            </div>
            <h1 className="text-3xl sm:text-4xl" data-testid="lesson-title">{l.title}</h1>
            <p className="mt-4 whitespace-pre-line text-osk-text" data-testid="lesson-description">{l.description}</p>
          </div>
          <div className="osk-card grid gap-4 p-5 sm:grid-cols-2">
            <p className="flex items-center gap-3 text-sm"><CalendarDays className="h-5 w-5 text-osk-blue" />{scheduleLabel(l, lang)}</p>
            <p className="flex items-center gap-3 text-sm"><Users className="h-5 w-5 text-osk-blue" />Max. {l.max_children} {tx("kinderen per les")}</p>
            <p className="flex items-center gap-3 text-sm"><Globe className="h-5 w-5 text-osk-blue" />{tx("Lestaal")}: {l.language}</p>
            <p className="flex items-center gap-3 text-sm"><Video className="h-5 w-5 text-osk-blue" />{tx("Live online, geen opnames")}</p>
          </div>
          <div className="osk-card p-5" data-testid="lesson-schedule">
            <h3 className="mb-3 text-lg">{t("detail.schedule")}</h3>
            <div className="divide-y divide-slate-100">
              {l.sessions.slice(0, 10).map((s) => (
                <div key={s.id} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-osk-muted" />{fmtFull(s.start, lang)} – {fmtTime(s.end)}</span>
                  <SeatsText n={s.seats_left} />
                </div>
              ))}
              {!l.sessions.length && <p className="py-2 text-sm text-osk-muted">{tx("Geen geplande lessen.")}</p>}
            </div>
            <p className="mt-3 text-xs text-osk-muted">{tx("Alle tijden in Atlantic Standard Time (Aruba, UTC−4).")}</p>
          </div>
          <div className="osk-card flex gap-4 p-5" data-testid="lesson-teacher">
            <Avatar fileId={l.teacher.photo_file_id} name={l.teacher.display_name} size={72} />
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-osk-muted">{t("detail.teacher")}</p>
              <p className="text-lg font-extrabold text-osk-navy">{l.teacher.display_name}</p>
              <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-emerald-600"><ShieldCheck className="h-4 w-4" />{tx("Geverifieerde docent")}</p>
              <p className="mt-2 text-sm text-osk-text">{l.teacher.bio}</p>
              <p className="mt-2 text-xs text-osk-muted">{tx("Vakken")}: {l.teacher.subjects?.join(", ")} · {tx("Talen")}: {l.teacher.languages?.join(", ")}</p>
            </div>
          </div>
        </div>
        <aside>
          <div className="osk-card sticky top-24 space-y-4 p-6" data-testid="booking-panel">
            <div className="flex items-end justify-between">
              <div><p className="text-3xl font-extrabold text-osk-navy" data-testid="lesson-price">{money(l.price, l.currency)}</p><p className="text-sm text-osk-muted">{tx("per kind per les")}</p></div>
              <SeatsText n={l.seats_left} testid="lesson-seats-left" />
            </div>
            <div className="space-y-2">
              <p className="osk-label">{t("detail.choose")}</p>
              {l.units.map((u) => <UnitOption key={u.key} u={u} l={l} selected={unit === u.key} onSelect={setUnit} lang={lang} />)}
              {!l.units.length && <p className="text-sm text-osk-muted">{tx("Er zijn momenteel geen plekken te boeken.")}</p>}
            </div>
            {!user && <Link to={`/inloggen?next=/lessen/${l.id}`} className="osk-btn-primary w-full" data-testid="login-to-book-btn">{t("detail.login_first")}</Link>}
            {user && user.role !== "parent" && <p className="rounded-xl bg-osk-light p-3 text-sm">{tx("Alleen ouders kunnen lessen boeken.")}</p>}
            {user?.role === "parent" && (children.length ? (
              <>
                <label className="block"><span className="osk-label">{t("detail.choose_child")}</span>
                  <select className="osk-input" value={child} onChange={(e) => setChild(e.target.value)} data-testid="booking-child-select">
                    {children.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.school_type} {c.grade})</option>)}
                  </select>
                </label>
                <button className="osk-btn-primary w-full" disabled={!unit || !child || busy} onClick={book} data-testid="book-lesson-btn">{t("detail.book")}</button>
              </>
            ) : <Link to="/ouder?tab=children" className="osk-btn-primary w-full" data-testid="add-child-first-btn">{t("detail.add_child")}</Link>)}
            <p className="text-xs text-osk-muted">{t("detail.nopay")}</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
